/**
 * Unified Login — runtime client.
 *
 * Node.js runtime support requires Node 22+.
 *
 * Load as an ES module:
 *   <script type="module" src="https://<unified-login-origin>/v1/unified-login-auth.js"></script>
 *   <script type="module">
 *     import { UnifiedLoginAuth } from 'https://<unified-login-origin>/v1/unified-login-auth.js';
 *     const auth = new UnifiedLoginAuth({ baseUrl: 'https://<unified-login-origin>', app: 'example-app' });
 *   </script>
 *
 * This is the foundation the  <hygiena-auth> web component builds its UI
 * shell on top of — not throwaway work. Runtime-agnostic on purpose: it has to
 * work in any downstream environment that provides fetch and URL support.
 *
 * The auth session can live in memory, localStorage, or sessionStorage, depending
 * on the storageType passed to the constructor. Memory is the default and the
 * fallback when the requested browser storage is unavailable. Stored state is an
 * opaque base64-encoded session object handled by the persistent state helper.
 */

/** Thrown by every rejected UnifiedLoginAuth operation. */
export class UnifiedLoginAuthError extends Error {
  /** @type {boolean} */
  mustReLogin;
  /** @type {TriggerType | undefined} */
  trigger;

  /**
   * @param {string} message
   * @param {number} status HTTP status Unified Login returned, or 0 for a network failure.
   * @param {{ mustReLogin?: boolean, cause?: unknown, trigger?: TriggerType }} [options]
   */
  constructor(message, status, options = {}) {
    super(message, { cause: options.cause });
    this.name = "UnifiedLoginAuthError";
    this.status = status;
    /** True only for the "genuinely can't refresh, log in again" case. */
    this.mustReLogin = options.mustReLogin ?? false;
    this.trigger = options.trigger;
  }
}

if (
  typeof process !== "undefined" &&
  typeof process.versions?.node === "string"
) {
  const majorVersion = Number.parseInt(process.versions.node.split(".")[0], 10);
  if (Number.isFinite(majorVersion) && majorVersion < 22) {
    throw new Error("UnifiedLoginAuth requires Node.js 22 or newer.");
  }
}

/** @typedef {"memory" | "localStorage" | "sessionStorage"} StorageType */
/** @typedef {"token-refresh-success" | "token-refresh-failed" } EventType */
/** @typedef {"login" | "token-refresh" | "logout" | "password-reset"} TriggerType */
/** @typedef {Record<string, string | number | boolean | null | undefined>} QueryParams */
/** @typedef {{ username?: string, email?: string, displayName?: string, role?: string, downstreamOrganizationId?: number, organizationName?: string, userCanEnableApps?: boolean, accessToken?: string, expiresIn?: number, tokenType?: string, message?: string, hasEmailVerified?: boolean,  }} AuthResponse */
/** @typedef {Pick<Required<AuthResponse>, "accessToken" | "expiresIn" | "tokenType" | "message"> & { expiresOn: number }} SessionToken */
/** @typedef {Omit<Required<AuthResponse>, keyof SessionToken>}  SessionAccount */
/** @typedef {{ account: SessionAccount | null, token: SessionToken | null, refreshAt: number }} SessionState */
/** @typedef {{ token: SessionToken, account: SessionAccount }} SessionInfo */
/** @typedef {{ response?: Response, data?: AuthResponse, trigger: TriggerType } & Partial<SessionInfo>} SessionStateUpdate */
/** @typedef {RequestInit & { queryParams?: QueryParams }} TransportRequestOptions */
/** @typedef {(customEvent?: CustomEvent) => void} EventHandler */

class PersistentState {
  /** @type {StorageType} */
  #storageType;
  /** @type {string} */
  #storageKey;
  /** @type {unknown} */
  #value;

  /**
   * @param {{ storageType?: StorageType, storageKey?: string, defaultValue?: unknown }} [config]
   */
  constructor({
    storageType = "memory",
    storageKey = "hygiena-auth-token",
    defaultValue = null,
  } = {}) {
    this.#storageType = storageType;
    this.#storageKey = storageKey;
    this.#value = defaultValue;

    this.#restoreStoredState();
  }

  get value() {
    return this.#value;
  }

  /** @param {unknown} nextValue */
  set value(nextValue) {
    this.#value = nextValue;
    this.#persistStoredState();
  }

  clear() {
    this.#value = null;
    this.#clearStoredState();
  }

  /** @returns {Storage | null} */
  #getStorage() {
    if (this.#storageType === "memory") {
      return null;
    }

    const storage = globalThis[this.#storageType];
    if (!storage || typeof storage.getItem !== "function") {
      return null;
    }

    return storage;
  }

  #restoreStoredState() {
    const storage = this.#getStorage();
    if (!storage) {
      return;
    }

    const rawState = storage.getItem(this.#storageKey);
    if (!rawState) {
      return;
    }

    try {
      this.#value = JSON.parse(this.#decodeStoredValue(rawState));
    } catch {
      this.#clearStoredState();
    }
  }

  #persistStoredState() {
    const storage = this.#getStorage();
    if (!storage) {
      return;
    }

    storage.setItem(
      this.#storageKey,
      this.#encodeStoredValue(JSON.stringify(this.#value)),
    );
  }

  #clearStoredState() {
    const storage = this.#getStorage();
    if (!storage) {
      return;
    }

    storage.removeItem(this.#storageKey);
  }

  /** @param {string} value */
  #encodeStoredValue(value) {
    return btoa(value);
  }

  /** @param {string} value */
  #decodeStoredValue(value) {
    return atob(value);
  }
}

class Transport {
  /** @type {string} */
  #baseUrl;

  /**
   *
   * @param {{ baseUrl: string }} param0
   */
  constructor({ baseUrl }) {
    this.#baseUrl = baseUrl;
  }

  /**
   *
   * @param {string} path
   * @param {TransportRequestOptions} [options]
   * @returns {Promise<Response>}
   */
  async request(path, options = {}) {
    const endpointUrl = new URL(path, this.#baseUrl);

    const { queryParams, ...fetchOptions } = options;
    for (const [key, value] of Object.entries(queryParams ?? {})) {
      if (value !== undefined && value !== null) {
        endpointUrl.searchParams.set(key, String(value));
      }
    }

    const fetchFn = this.#getFetch();
    const headers = this.#normalizeHeaders(fetchOptions.headers);
    return fetchFn(endpointUrl, { ...fetchOptions, headers });
  }

  /**
   *
   * @param {string} path
   * @param {Record<string,unknown>} payload
   * @param {TransportRequestOptions} [options]
   * @returns {Promise<Response>}
   */
  async postJson(path, payload, options = {}) {
    const body = JSON.stringify(payload);

    const headers = new Headers(options.headers ?? undefined);
    headers.set("Content-Type", "application/json;charset=UTF-8");

    return this.request(path, {
      ...options,
      method: "POST",
      headers,
      body,
    });
  }

  /**
   *
   * @param {string} path
   * @param {Record<string,unknown>} payload
   * @param {TransportRequestOptions} [options]
   * @returns {Promise<Response>}
   */
  async postForm(path, payload, options = {}) {
    const body = new URLSearchParams();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined && value !== null) {
        body.set(key, String(value));
      }
    }

    const headers = new Headers(options.headers ?? undefined);
    headers.set(
      "Content-Type",
      "application/x-www-form-urlencoded;charset=UTF-8",
    );

    return this.request(path, {
      ...options,
      method: "POST",
      headers,
      body,
    });
  }

  /** @returns {typeof fetch} */
  #getFetch() {
    if (typeof globalThis.fetch !== "function") {
      throw new UnifiedLoginAuthError(
        "fetch is not available in this runtime.",
        0,
      );
    }

    return globalThis.fetch.bind(globalThis);
  }

  /** @param {HeadersInit | undefined} headersInit */
  #normalizeHeaders(headersInit) {
    const headers = {};

    if (!headersInit) {
      return headers;
    }

    if (Array.isArray(headersInit)) {
      for (const [key, value] of headersInit) {
        headers[key] = value;
      }
      return headers;
    }

    if (typeof Headers !== "undefined" && headersInit instanceof Headers) {
      headersInit.forEach((value, key) => {
        headers[key] = value;
      });
      return headers;
    }

    return { ...headersInit };
  }
}

class EventHub {
  /** @type {Map<string, Set<EventHandler>>} */
  #listeners;

  constructor() {
    this.#listeners = new Map();
  }

  /**
   * @param {string} event
   * @param {EventHandler} handler
   * @returns {() => boolean | undefined}
   */
  on(event, handler) {
    if (!this.#listeners.has(event)) this.#listeners.set(event, new Set());
    this.#listeners.get(event).add(handler);
    return () => this.#listeners.get(event)?.delete(handler);
  }

  /**
   * @param {string} event
   * @param {CustomEvent} customEvent
   */
  emit(event, customEvent) {
    for (const handler of this.#listeners.get(event) ?? [])
      handler(customEvent);
  }
}

class ManagedTimer {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timerId;

  constructor() {
    this.#timerId = null;
  }

  /**
   * @param {number} delayMs
   * @param {() => void} callback
   */
  schedule(delayMs, callback) {
    this.cancel();
    this.#timerId = setTimeout(() => {
      this.#timerId = null;
      callback();
    }, delayMs);
  }

  cancel() {
    if (this.#timerId) {
      clearTimeout(this.#timerId);
      this.#timerId = null;
    }
  }
}

export class UnifiedLoginAuth {
  /** @type {string} */
  #appName;
  /** @type {ManagedTimer} */
  #refreshTimer;
  /** @type {Promise<SessionStateUpdate> | null} */
  #refreshPromise;
  /** @type {PersistentState} */
  #state;
  /** @type {EventHub} */
  #events;
  /** @type {Transport} */
  #transport;

  /**
   * @param {{ baseUrl?: string, appName: string, storageType?: StorageType, storageKey?: string }} config
   *   baseUrl - Unified Login's origin, e.g. 'https://unified-login.example.com'. Default to '' (same-origin), matching the Client/ dev test page's own setup.
   *   appName - the RegisteredApp name this instance mints tokens for (e.g. 'example-app').
   *   storageType - where to store the access token. Defaults to 'memory'.
   *   storageKey - the key under which to store the access token. Defaults to 'hygiena-auth-token'.
   */
  constructor(config) {
    const { baseUrl = "", appName } = config;
    if (!appName) throw new Error("UnifiedLoginAuth requires an `appName`.");

    this.#appName = appName;

    this.#state = new PersistentState({
      storageType: config.storageType ?? "memory",
      storageKey: config.storageKey ?? "hygiena-auth-token",
      defaultValue: {
        account: null,
        token: null,
        refreshAt: 0,
      },
    });

    this.#refreshTimer = new ManagedTimer();
    /** Single-flights concurrent refreshes — see #refresh()'s remarks. */
    this.#refreshPromise = null;
    this.#events = new EventHub();
    this.#transport = new Transport({
      baseUrl: baseUrl.replace(/\/$/, ""),
    });

    if (this.#state.value?.token) {
      const refreshInMs = this.#state.value.refreshAt - Date.now();
      this.#scheduleProactiveRefresh(refreshInMs);
    }
  }

  // --- public API ---

  get isAuthenticated() {
    return this.#state.value?.token !== null;
  }

  /**
   * @type {SessionState}
   */
  get state() {
    return this.#state.value;
  }

  get username() {
    return this.#state.value?.account?.username;
  }

  get displayName() {
    return this.#state.value?.account?.displayName;
  }

  /**
   * Signs in with a username (username or plain username) + password and starts this
   * instance's session. Throws UnifiedLoginAuthError on failure (bad credentials, etc.).
   *
   * @param {{ username: string, password: string }} payload
   * @returns {Promise<SessionStateUpdate>}
   */
  async login(payload) {
    const response = await this.#transport.postForm("/auth/login", payload, {
      queryParams: { app: this.#appName },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new UnifiedLoginAuthError(
        data.message || "Login failed.",
        response.status,
        { trigger: "login", cause: response },
      );
    }

    /** @type {AuthResponse} */
    const data = await response.json();
    const refreshInMs = this.#applySession(data);
    this.#scheduleProactiveRefresh(refreshInMs);

    return {
      trigger: "login",
      response,
      data,
      token: this.#state.value.token,
      account: this.#state.value.account,
    };
  }

  /**
   * Clears the in-memory session. Does not call the server — there is no server session to end.
   *
   * @returns {Promise<SessionStateUpdate>}
   */
  async logout() {
    this.#state.clear();
    this.#refreshTimer.cancel();

    /** @type {SessionStateUpdate} */
    return {
      trigger: "logout",
      response: undefined,
      data: undefined,
      token: this.#state.value?.token,
      account: this.#state.value?.account,
    };
  }

  /**
   *
   * @param {{ token: string, newPassword: string }} payload
   * @returns {Promise<SessionStateUpdate>}
   */
  async resetPassword(payload) {
    const response = await this.#transport.postJson(
      "/auth/password-reset",
      payload,
      {
        queryParams: { app: this.#appName },
      },
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new UnifiedLoginAuthError(
        data.message || "Password reset failed.",
        response.status,
        { trigger: "password-reset", cause: response },
      );
    }

    /** @type {Pick<AuthResponse, "email">} */
    const data = await response.json();
    const refreshInMs = this.#applySession(data);
    this.#scheduleProactiveRefresh(refreshInMs);

    return {
      trigger: "password-reset",
      response,
      data,
      token: this.#state.value?.token,
      account: this.#state.value?.account,
    };
  }

  /**
   * The one method downstream code needs to know. Returns the current access
   * token if it's still comfortably valid; transparently refreshes first if it's
   * near expiry (or already expired), then returns the fresh one.
   *
   * Throws UnifiedLoginAuthError if there's no session, or if refreshing fails —
   * check `error.mustReLogin` to tell "the user has to log in again" apart from
   * a transient failure (network blip, Unified Login briefly unreachable), which is
   * usually worth a retry instead of a re-login prompt.
   *
   * @returns {Promise<SessionInfo>} The access token.
   */
  async getSession() {
    const session = this.#state.value;
    if (session?.token && Date.now() < session.refreshAt) {
      return {
        account: session.account,
        token: session.token,
      };
    }
    return await this.#refresh();
  }

  /**
   *
   * @param {string} accessToken
   * @returns {Promise<SessionStateUpdate>}
   */
  async setSession(accessToken) {
    return this.#doRefresh(accessToken);
  }

  /**
   * Optional fetch wrapper that auto-attaches the Authorization: Bearer header,
   * so downstream code doesn't have to call getSession() manually at every
   * call site. Everything else about `fetch` behaves as normal.
   *
   * @param {string} path
   * @param {TransportRequestOptions} [options]
   * @returns {Promise<Response>}
   */
  async fetch(path, options = {}) {
    const token = await this.#getAccessToken();
    const headers = new Headers(options.headers ?? undefined);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const requestOptions = {
      ...options,
      queryParams: {
        app: this.#appName,
      },
      headers,
    };

    return this.#transport.request(path, requestOptions);
  }

  /**
   * Subscribes to a UnifiedLoginAuth event. Currently emits:
   *   'token-refresh-failed' — the session could not be refreshed and genuinely needs
   *     a fresh login; this is what the <hygiena-auth> web component hooks
   *     into to re-prompt for credentials.
   * Returns an unsubscribe function.
   * @param {EventType} event
   * @param {EventHandler} handler
   * @returns {() => boolean | undefined}
   */
  on(event, handler) {
    return this.#events.on(event, handler);
  }

  // --- internal ---

  /**
   * @returns {string}
   */
  async #getAccessToken() {
    const session = await this.getSession();
    return session.token.accessToken;
  }

  /**
   * @param {EventType} eventType
   * @param {unknown} detail
   */
  #emit(eventType, detail) {
    const customEvent = new CustomEvent(eventType, { detail });
    this.#events.emit(eventType, customEvent);
  }

  /** @param {number} delayMs */
  #scheduleProactiveRefresh(delayMs) {
    this.#refreshTimer.schedule(delayMs, () => {
      // Errors are already surfaced via the 'token-refresh-failed' event / the
      // rejected promise a concurrent getSession() caller would see; nothing
      // further to do with this proactive, no-caller-waiting attempt.
      this.#refresh().catch(() => {});
    });
  }

  /**
   * Single-flights concurrent refreshes — the most common bug in hand-rolled
   * refresh logic. If several callers ask for a token at once right as it nears
   * expiry, one shared in-flight promise serves all of them, not N simultaneous
   * calls to /auth/refresh.
   */
  /** @returns {Promise<SessionStateUpdate>} */
  #refresh() {
    if (!this.#refreshPromise) {
      /** @type {SessionState | null} */
      const session = this.#state.value;
      if (!session?.token) {
        const err = new UnifiedLoginAuthError("No session to refresh.", 401, {
          mustReLogin: true,
          trigger: "token-refresh",
        });
        this.#emit("token-refresh-failed", err);
        throw err;
      }

      this.#refreshPromise = this.#doRefresh(session.token.accessToken).finally(
        () => {
          this.#refreshPromise = null;
        },
      );
    }
    return this.#refreshPromise;
  }

  /**
   * Performs the token refresh operation.
   * @param {string} accessToken
   * @returns {Promise<SessionStateUpdate>} */
  async #doRefresh(accessToken) {
    let response;
    try {
      response = await this.#transport.request("/auth/refresh", {
        method: "POST",
        queryParams: { app: this.#appName },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (cause) {
      // Network failure — transient, not "must re-login". The existing token
      // (and its schedule) is left alone so a retry can still succeed before it
      // actually expires.
      throw new UnifiedLoginAuthError(
        "Could not reach Unified Login to refresh the session.",
        0,
        { cause, mustReLogin: false, trigger: "token-refresh" },
      );
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.#state.clear();
      this.#refreshTimer.cancel();
      const err = new UnifiedLoginAuthError(
        data.message || "Session expired.",
        response.status,
        { cause: response, mustReLogin: true, trigger: "token-refresh" },
      );
      this.#emit("token-refresh-failed", err);
      throw err;
    }

    /** @type {AuthResponse} */
    const data = await response.json();
    const refreshInMs = this.#applySession(data);
    this.#scheduleProactiveRefresh(refreshInMs);

    /** @type {SessionStateUpdate} */
    const detail = {
      trigger: "token-refresh",
      response,
      data,
      token: this.#state.value.token,
      account: this.#state.value.account,
    };
    this.#emit("token-refresh-success", detail);

    return detail;
  }

  /**
   * @param {AuthResponse} data
   * @returns {number}
   */
  #applySession(data) {
    const { accessToken, expiresIn, tokenType, message, ...account } = data;
    const expiresInOrDefault = typeof expiresIn === "number" ? expiresIn : 3600;
    const refreshInMs = Math.max(expiresInOrDefault * 1000 * 0.8, 0);
    const refreshAt = Date.now() + refreshInMs;
    // TODO (JM): remove when unified-login provides username
    const username = account.username ?? account.email;

    this.#state.value = {
      account: {
        ...account,
        displayName: account.displayName ?? username,
      },
      token: {
        accessToken,
        message,
        expiresIn: expiresInOrDefault,
        expiresOn: refreshAt,
        tokenType: tokenType ?? "Bearer",
      },
      refreshAt,
    };

    return refreshInMs;
  }
}
